import styled, { css } from "styled-components";
import { theme } from "../../../styles/theme";
import { useMediaQuery } from "react-responsive";
import { ArrowLeftOutlined, BackwardOutlined, CheckOutlined } from "@ant-design/icons";
import { useEffect } from "react";
import { Div, Text } from "../../../styles/Common";

const StepContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: 0 0 32px 0;
  position: relative;
  width: 100%;
  max-width: 800px;
  padding: 0 16px;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 20px;
    padding: 0 16px;
    margin: 0 0 20px 0;
  }
`;

const StepConnector = styled.div`
  position: absolute;
  top: 16px;
  left: 0;
  right: 0;
  height: 2px;
  background: linear-gradient(90deg, ${theme.colors.gray200} 0%, rgba(200, 200, 200, 0.6) 100%);
  border-radius: 1px;
  z-index: 1;

  @media (max-width: 768px) {
    top: 0;
    bottom: 0;
    left: 18px;
    width: 3px;
    height: auto;
    background: linear-gradient(180deg, rgba(220, 38, 127, 0.1) 0%, rgba(200, 200, 200, 0.3) 100%);
    border-radius: 2px;
  }
`;

const ProgressConnector = styled.div`
  position: absolute;
  top: 16px;
  left: 0;
  height: 2px;
  background: linear-gradient(90deg, ${theme.colors.red} 0%, #ff4757 100%);
  border-radius: 1px;
  z-index: 2;
  transition: all 0.5s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  box-shadow: 0 1px 3px rgba(220, 38, 127, 0.4);

  @media (max-width: 768px) {
    top: 0;
    left: 18px;
    width: 3px;
    height: 0;
    background: linear-gradient(180deg, ${theme.colors.red} 0%, #ff6b6b 100%);
    transition: height 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    box-shadow: 0 0 8px rgba(220, 38, 127, 0.5);
    border-radius: 2px;
  }
`;

const StepItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  z-index: 3;
  cursor: ${({ $clickable }) => ($clickable ? "pointer" : "default")};
  transition: transform 0.2s ease;

  ${({ $clickable }) =>
    $clickable &&
    css`
      &:hover {
        transform: translateY(-1px);
      }
    `}

  @media (max-width: 768px) {
    flex-direction: row;
    align-items: center;
    gap: 14px;
    width: 100%;
    padding: 12px 16px;
    margin: 0;
    border-radius: 12px;
    background: ${({ $active, $completed }) =>
      $active 
        ? 'linear-gradient(135deg, rgba(220, 38, 127, 0.15) 0%, rgba(255, 107, 107, 0.08) 100%)'
        : $completed
        ? 'linear-gradient(135deg, rgba(220, 38, 127, 0.08) 0%, rgba(255, 107, 107, 0.05) 100%)'
        : 'transparent'};
    border: ${({ $active }) => $active ? '1px solid rgba(220, 38, 127, 0.2)' : '1px solid transparent'};
    backdrop-filter: ${({ $active, $completed }) => ($active || $completed) ? 'blur(10px)' : 'none'};
    transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);

    ${({ $clickable }) =>
      $clickable &&
      css`
        &:hover {
          transform: translateX(4px);
          background: linear-gradient(135deg, rgba(220, 38, 127, 0.12) 0%, rgba(255, 107, 107, 0.06) 100%);
          box-shadow: 0 4px 12px rgba(220, 38, 127, 0.15);
        }
      `}
  }
`;

const StepCircle = styled.div`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 14px;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  position: relative;
  overflow: hidden;

  ${({ $active, $completed }) => css`
    background: ${$active || $completed
      ? `linear-gradient(135deg, ${theme.colors.red} 0%, #ff4757 100%)`
      : `linear-gradient(135deg, ${theme.colors.gray200} 0%, #d1d5db 100%)`};
    color: ${theme.colors.white};
    transform: ${$active ? "scale(1.15)" : "scale(1)"};
    border: ${$active ? `2px solid rgba(220, 38, 127, 0.3)` : "1px solid transparent"};
    box-shadow: ${$active || $completed
      ? "0 4px 12px rgba(220, 38, 127, 0.3)"
      : "0 2px 6px rgba(0, 0, 0, 0.1)"};
  `}

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: linear-gradient(135deg, rgba(255, 255, 255, 0.2) 0%, transparent 70%);
    border-radius: 50%;
    opacity: ${({ $active, $completed }) => ($active || $completed ? 1 : 0.7)};
  }

  @media (max-width: 480px) {
    width: 30px;
    height: 30px;
    font-size: 13px;
  }

  @media (max-width: 768px) {
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    font-size: 15px;
  }
`;

const StepLabel = styled.div`
  font-size: ${theme.fonts.size.h5};
  margin-top: 8px;
  text-align: center;
  max-width: 120px;
  font-weight: ${({ $active }) => ($active ? "600" : "400")};
  color: ${({ $active, $completed }) =>
    $active || $completed ? theme.colors.red : theme.colors.white};
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  line-height: 1.2;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;

  ${({ $active }) =>
    $active &&
    css`
      text-shadow: 0 1px 2px rgba(220, 38, 127, 0.2);
    `}

  @media (max-width: 768px) {
    margin-top: 0;
    margin-left: 0;
    text-align: left;
    flex: 1;
    white-space: normal;
    overflow: visible;
    font-size: 15px;
    line-height: 1.3;
    font-weight: ${({ $active }) => ($active ? "600" : "500")};
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const BackButton = styled(Text)`
  cursor: pointer;
  margin-bottom: 5%;
  display: inline-flex;
  align-items: center;
  margin: 20px 0 20px 0;
  gap: 6px;
  border-radius: 8px;
  transition: all 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94);
  font-weight: 500;

  &:hover {
    transform: translateX(-2px);
  }

  @media (max-width: 768px) {
    gap: 8px;
  }
`;

const CheckIcon = styled(CheckOutlined)`
  font-size: 14px;
  position: relative;
  z-index: 1;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.2));

  @media (max-width: 768px) {
    font-size: 16px;
  }

  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

const StepNumber = styled.span`
  position: relative;
  z-index: 1;
  font-weight: 600;
  filter: drop-shadow(0 1px 1px rgba(0, 0, 0, 0.1));
`;

const stepLabels = ["Datos Personales", "Ubicación y domicilio", "Hogar y estilo de vida", "Intereses y hábitos"];

export function StepProgress({ currentStep, setCurrentStep, setStep }) {
  const isMobile = useMediaQuery({ maxWidth: 768 });
  const totalSteps = stepLabels.length;

  const progressPercentage = ((currentStep - 1) / (totalSteps - 1)) * 100;

  useEffect(() => {
    setStep(stepLabels[(currentStep - 1)]);
  }, [currentStep]);

  const getStepState = (index) => {
    const stepNumber = index + 1;
    return {
      stepNumber,
      isActive: currentStep === stepNumber,
      isCompleted: currentStep > stepNumber,
      isClickable: stepNumber <= currentStep,
    };
  };

  return (
    <Div direction="column">
      <StepContainer>
        <StepConnector />
        <ProgressConnector
          style={
            isMobile
              ? { height: `${progressPercentage}%` }
              : { width: `${progressPercentage}%` }
          }
        />

        {stepLabels.map((label, index) => {
          const { stepNumber, isActive, isCompleted, isClickable } =
            getStepState(index);

          return (
            <StepItem
              key={stepNumber}
              $clickable={isClickable}
              role="button"
              tabIndex={isClickable ? 0 : -1}
              aria-disabled={!isClickable}
              aria-label={`Paso ${stepNumber}: ${label}${isActive ? ' (actual)' : ''}${isCompleted ? ' (completado)' : ''}`}
              title={label}
            >
              <StepCircle $active={isActive} $completed={isCompleted}>
                {isCompleted ? (
                  <CheckIcon />
                ) : (
                  <StepNumber>{stepNumber}</StepNumber>
                )}
              </StepCircle>
              {isMobile && <StepLabel $active={isActive} $completed={isCompleted}>
                {label}
              </StepLabel>}
            </StepItem>
          );
        })}
      </StepContainer>
      
      {currentStep > 1 && (
        <BackButton 
          onClick={() => {setCurrentStep(currentStep - 1)}} 
          color={theme.colors.red}
        >
          <ArrowLeftOutlined/> Regresar
        </BackButton>
      )}
    </Div>
  );
}